import { useEffect, useMemo, useRef } from 'react'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { CompletionScreen } from '@/src/components/ui/CompletionScreen'
import { useRequiredAppSessionUser } from '@/src/hooks/useRequiredAppSessionUser'
import { useTranslation } from 'react-i18next'

import type { DashboardExerciseType } from '../../../types/dashboard'
import {
  buildSessionLifecycleProperties,
  MixpanelEvents,
  trackEvent,
} from '../../../services/analytics'
import { getDisplayFirstName } from '../../../utils/user-name'
import { useSessionCompletionStatus } from '../hooks/useSessionCompletionMutation'

const SESSION_FINISH_DASHBOARD_ROUTE = '/(protected)/(account)/(tabs)' as const
const SESSION_FINISH_LIBRARY_ROUTE =
  '/(protected)/(account)/(tabs)/library' as const

type FinishSource = 'session' | 'preview' | 'library'

function normalizeParam(
  value: string | string[] | undefined,
): string | undefined {
  if (Array.isArray(value)) return value[0]
  return value
}

function normalizeSource(value: string | string[] | undefined): FinishSource {
  const source = normalizeParam(value)
  if (source === 'preview' || source === 'library') return source
  return 'session'
}

export default function SessionFinishScreen() {
  const router = useRouter()
  const { t } = useTranslation(['session'])
  const { authUser, backendUser } = useRequiredAppSessionUser()
  const trackedCompletionSessionIdRef = useRef<string | null>(null)
  const {
    id: idParam,
    source: sourceParam,
    title: titleParam,
    weekId: weekIdParam,
    subjectId: subjectIdParam,
    programId: programIdParam,
    exerciseType: exerciseTypeParam,
  } = useLocalSearchParams<{
    id?: string | string[]
    source?: string | string[]
    title?: string | string[]
    weekId?: string | string[]
    subjectId?: string | string[]
    programId?: string | string[]
    exerciseType?: string | string[]
  }>()

  const sessionId = normalizeParam(idParam)
  const sessionTitle = normalizeParam(titleParam) ?? ''
  const source = normalizeSource(sourceParam)
  const weekId = normalizeParam(weekIdParam)
  const subjectId = normalizeParam(subjectIdParam)
  const programId = normalizeParam(programIdParam)
  const exerciseType = normalizeParam(exerciseTypeParam) as
    DashboardExerciseType | undefined
  const completionStatus = useSessionCompletionStatus()
  const isSavingProgress = completionStatus === 'pending'
  const destinationRoute =
    source === 'library'
      ? SESSION_FINISH_LIBRARY_ROUTE
      : SESSION_FINISH_DASHBOARD_ROUTE

  useEffect(() => {
    if (
      completionStatus !== 'success' ||
      sessionId == null ||
      trackedCompletionSessionIdRef.current === sessionId
    ) {
      return
    }

    trackedCompletionSessionIdRef.current = sessionId
    trackEvent(
      exerciseType === 'daily'
        ? MixpanelEvents.DailyRoutineFinished
        : MixpanelEvents.CourseFinished,
      buildSessionLifecycleProperties({
        sessionId,
        sessionName: sessionTitle,
        weekId,
        subjectId,
        programId,
        exerciseType: exerciseType,
      }),
    )
  }, [
    completionStatus,
    exerciseType,
    programId,
    sessionId,
    sessionTitle,
    subjectId,
    weekId,
  ])

  const buttonLabel = useMemo(() => {
    return source === 'library'
      ? t('session:back_to_library')
      : t('session:back_to_dashboard')
  }, [source, t])

  const displayFirstName = useMemo(() => {
    return getDisplayFirstName({
      authUser,
      backendFirstName: backendUser?.firstName,
    })
  }, [authUser, backendUser?.firstName])

  const handleNavigate = () => {
    if (isSavingProgress) return
    router.replace(destinationRoute)
  }

  return (
    <CompletionScreen
      heading={t('session:finish_heading', { name: displayFirstName })}
      subheading={t('session:finish_subheading')}
      buttonLabel={buttonLabel}
      isLoading={isSavingProgress}
      onPress={handleNavigate}
    />
  )
}
